import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api, requisitar, type Pagina } from '../../lib/api'
import { chaves } from '../../lib/query'
import type {
  Pendencia,
  ResultadoReprocesso,
  ResumoPendencias,
  Upload,
} from '../../lib/tipos'

export function useUploads() {
  return useQuery({
    queryKey: chaves.uploads,
    queryFn: () => api.obter<Pagina<Upload>>('/uploads/'),
    // O upload é síncrono, mas o histórico é o lugar onde o operador confere o
    // resultado — vale revalidar ao voltar para a aba.
    staleTime: 30 * 1000,
  })
}

export function useUpload(id: number) {
  return useQuery({
    queryKey: chaves.upload(id),
    queryFn: () => api.obter<Upload>(`/uploads/${id}/`),
  })
}

export function useResumoPendencias(id: number) {
  return useQuery({
    queryKey: chaves.resumoPendencias(id),
    queryFn: () => api.obter<ResumoPendencias>(`/uploads/${id}/pendencias/`),
  })
}

export type FiltrosPendencia = {
  upload?: number
  motivo?: string
  resolvida?: boolean
  pagina: number
}

export function usePendencias(filtros: FiltrosPendencia) {
  return useQuery({
    queryKey: chaves.pendencias(filtros),
    queryFn: () =>
      api.obter<Pagina<Pendencia>>('/pendencias/', {
        upload: filtros.upload,
        motivo: filtros.motivo,
        resolvida: filtros.resolvida === undefined ? undefined : String(filtros.resolvida),
        page: filtros.pagina,
      }),
    placeholderData: (anterior) => anterior,
  })
}

/**
 * Envio da planilha.
 *
 * `multipart`, e por isso vai por `requisitar` direto: o `api.criar` serializa
 * JSON. Sucesso invalida uploads **e** pendências — o arquivo novo muda as
 * duas listas, e deixar a fila de pendências mostrando o número velho depois de
 * um upload é o tipo de desatualização silenciosa que este projeto existe para
 * eliminar.
 */
export function useEnviarPlanilha() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (arquivo: File) => {
      const formulario = new FormData()
      formulario.append('arquivo', arquivo)
      return requisitar<Upload>('/ingestao/upload', { metodo: 'POST', corpo: formulario })
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.uploads })
      void cliente.invalidateQueries({ queryKey: ['ingestao', 'pendencias'] })
    },
  })
}

/**
 * Reprocessa só as linhas pendentes do upload.
 *
 * É o fecho do ciclo: o operador vê a pendência, cria o de-para que faltava e
 * manda rodar de novo — sem pedir o arquivo outra vez, porque o staging o
 * guardou.
 */
export function useReprocessar(id: number) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: () => api.criar<ResultadoReprocesso>(`/uploads/${id}/reprocessar/`, {}),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.upload(id) })
      void cliente.invalidateQueries({ queryKey: chaves.resumoPendencias(id) })
      void cliente.invalidateQueries({ queryKey: chaves.uploads })
      void cliente.invalidateQueries({ queryKey: ['ingestao', 'pendencias'] })
    },
  })
}

/**
 * Exclui um upload — e com ele a importação inteira.
 *
 * O backend apaga os fatos derivados (custo, captação, preço de venda) na mesma
 * transação, então isto desfaz de verdade o que aquele arquivo trouxe. Invalida
 * uploads **e** pendências: some da fila de trabalho junto com o histórico.
 */
export function useRemoverUpload() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.remover(`/uploads/${id}/`),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.uploads })
      void cliente.invalidateQueries({ queryKey: ['ingestao', 'pendencias'] })
    },
  })
}

// ─────────────────────────── De-paras ────────────────────────────────────

export type TipoDePara = 'produto' | 'distribuidora' | 'praca' | 'base'

export const CAMINHO_DEPARA: Record<TipoDePara, string> = {
  produto: '/de-paras/produto/',
  distribuidora: '/de-paras/distribuidora/',
  praca: '/de-paras/praca/',
  base: '/de-paras/base/',
}

export function useDeParas<T>(tipo: TipoDePara, busca: string, pagina: number) {
  return useQuery({
    queryKey: chaves.deParas(tipo, busca, pagina),
    queryFn: () => api.obter<Pagina<T>>(CAMINHO_DEPARA[tipo], { busca, page: pagina }),
    placeholderData: (anterior) => anterior,
  })
}

/**
 * Criar, editar e apagar de-para.
 *
 * Invalida as pendências junto: criar o de-para que faltava é justamente o que
 * torna uma pendência resolvível, e a fila precisa refletir isso na hora — ou o
 * operador cadastra o mesmo mapeamento duas vezes achando que não pegou.
 */
export function useSalvarDePara(tipo: TipoDePara) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: ({ id, dados }: { id?: number; dados: Record<string, unknown> }) =>
      id === undefined
        ? api.criar(CAMINHO_DEPARA[tipo], dados)
        : api.atualizar(`${CAMINHO_DEPARA[tipo]}${id}/`, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['ingestao', 'de-paras', tipo] })
      void cliente.invalidateQueries({ queryKey: ['ingestao', 'pendencias'] })
    },
  })
}

export function useRemoverDePara(tipo: TipoDePara) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.remover(`${CAMINHO_DEPARA[tipo]}${id}/`),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['ingestao', 'de-paras', tipo] })
      void cliente.invalidateQueries({ queryKey: ['ingestao', 'pendencias'] })
    },
  })
}
